import { useEffect, useState } from 'react';
import { Button, Empty, Table, TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

// services
import NotificationService from 'core/services/notification';
import { onDeleteCity, onGetCities } from 'modules/cities/services/cities';

// models
import { City } from 'modules/cities/models/city';

// components
import Icon from 'utils/icon';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';
import { isRTL } from './../../../../core/services/language';

function ListCitiesPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [cities, setCities] = useState<City[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [tempDeletedCity, setTempDeletedCity] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<City>['columns'] = [
		{
			title: t('CITIES.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <p className="mb-0 fw-medium">{isRTL() ? name.ar : name.en}</p>,
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'active',
			key: 'active',
			render: (active) => (
				<span className={`status fw-medium ${active ? 'active' : 'inactive'}`}>{active ? t('COMMON.ACTIVE') : t('COMMON.INACTIVE')}</span>
			),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, city) => (
				<div className="d-flex gap-2">
					<AuthActionComponent action="city_update">
						<Link to={`${city.id}`}>
							<Icon name="edit" width={35} height={35} />
						</Link>
					</AuthActionComponent>
					<AuthActionComponent action="city_delete">
						<Button type="link" className="p-0" onClick={() => onShowConfirmation(city.id)}>
							<Icon name="delete" width={35} height={35} />
						</Button>
					</AuthActionComponent>
				</div>
			),
		},
	];

	useEffect(() => {
		getCities();
	}, []);

	const getCities = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCities();
			setCities(response.data.data.cities);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetCities(page);
			setCities(response.data.data.cities);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoadMore(false);
		}
	};

	const onShowConfirmation = (cityId: number) => {
		setShowConfirmation(true);
		setTempDeletedCity(cityId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteCity(tempDeletedCity as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('CITIES.DELETED_SUCCESS'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getCities();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.OPS'), t('CITIES.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('CITIES.MANAGEMENT_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5>
								{totalRecords} {t('CITIES.MANAGEMENT_COUNT')}
							</h5>
							<p>{t('CITIES.MANAGEMENT_DESCRIPTION')}</p>
						</div>
						<AuthActionComponent action="city_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={cities}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>

						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}
			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('CITIES.DELETE_TITLE')}
				description={t('CITIES.DELETE_DESCRIPTION')}
				note={t('CITIES.DELETE_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListCitiesPage;
